import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { collection, getDocs } from "firebase/firestore";
import { toast } from "sonner";

import { DateRangePicker } from "../../../components/DateRangePicker";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { DataTable } from "../../../components/ui/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Spinner } from "../../../components/ui/spinner";
import { db } from "../../../firebase";
import { ALL_AFFILIATION, LOG_LEVEL_TEXT, RUNNING_FRAMES } from "./constants";
import {
  getMonthlyCuration,
  saveMonthlyCuration,
} from "./persistence";
import {
  buildDatasetSummary,
  loadCoupangReferences,
  loadSupplementalCurationData,
  loadSalesRows,
  runDebate,
} from "./services";
import {
  CURATION_TABLE_COLUMNS,
  CUSTOMER_CURATION_TABLE_COLUMNS,
  CurationTableRow,
} from "./tableColumns";
import {
  DatasetSummary,
  DebateResult,
  LogLevel,
  MonthlyCurationRecord,
  RunLogItem,
} from "./types";
import { money, sanitizeCustomerFacingReason } from "./utils";

interface AICurationPageProps {
  lockedAffiliation?: string;
  hideFilters?: boolean;
  forceAllAffiliations?: boolean;
  forceAllPeriods?: boolean;
  customerFacing?: boolean;
}

const AICurationPage: React.FC<AICurationPageProps> = ({
  lockedAffiliation = "",
  hideFilters = false,
  forceAllAffiliations = false,
  forceAllPeriods = false,
  customerFacing = false,
}) => {
  const fixedAffiliation = customerFacing
    ? lockedAffiliation.trim()
    : forceAllAffiliations
      ? ""
      : lockedAffiliation.trim();
  const isAffiliationLocked =
    customerFacing || (!forceAllAffiliations && fixedAffiliation.length > 0);
  const [affiliations, setAffiliations] = useState<string[]>([]);
  const [selectedAff, setSelectedAff] = useState<string>(
    customerFacing
      ? fixedAffiliation
      : forceAllAffiliations
        ? ALL_AFFILIATION
        : fixedAffiliation || ALL_AFFILIATION,
  );
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    forceAllPeriods
      ? null
      : [dayjs().startOf("month"), dayjs().endOf("day")],
  );
  const [selectedMonthKey, setSelectedMonthKey] = useState(
    dayjs().format("YYYY-MM"),
  );
  const [requirement, setRequirement] = useState("");
  const [running, setRunning] = useState(false);
  const [datasetSummary, setDatasetSummary] = useState<DatasetSummary | null>(
    null,
  );
  const [debateResult, setDebateResult] = useState<DebateResult | null>(null);
  const [savedMonthlyCuration, setSavedMonthlyCuration] =
    useState<MonthlyCurationRecord | null>(null);
  const [loadingSavedMonthlyCuration, setLoadingSavedMonthlyCuration] =
    useState(false);
  const [savingMonthlyCuration, setSavingMonthlyCuration] = useState(false);
  const [runLogs, setRunLogs] = useState<RunLogItem[]>([]);
  const [activeStepMessage, setActiveStepMessage] = useState("");
  const [runningFrameIndex, setRunningFrameIndex] = useState(0);
  const logContainerRef = useRef<HTMLDivElement | null>(null);

  const appendLog = (level: LogLevel, message: string) => {
    const now = new Date();
    const at = `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes(),
    ).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
    const item: RunLogItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      at,
      level,
      message,
    };
    setRunLogs((prev) => [...prev, item].slice(-200));
  };

  const clearLogs = () => setRunLogs([]);

  const openAIEnabled = true;
  const openAIApiKey = "";
  const openAIModel = (
    process.env.REACT_APP_OPENAI_MODEL || "gpt-5-mini"
  ).trim();
  const openAIFallbackModel = (
    process.env.REACT_APP_OPENAI_FALLBACK_MODEL || "gpt-4.1-mini"
  ).trim();
  const geminiApiKey = (process.env.REACT_APP_GEMINI_API_KEY || "").trim();
  const geminiModel = (
    process.env.REACT_APP_GEMINI_MODEL || "gemini-1.5-pro"
  ).trim();
  const geminiFallbackModel = (
    process.env.REACT_APP_GEMINI_FALLBACK_MODEL || ""
  ).trim();

  useEffect(() => {
    if (!logContainerRef.current) return;
    logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
  }, [runLogs, running]);

  useEffect(() => {
    if (!running || !activeStepMessage) return;
    const timer = window.setInterval(() => {
      setRunningFrameIndex((prev) => (prev + 1) % 1200);
    }, 450);
    return () => window.clearInterval(timer);
  }, [running, activeStepMessage]);

  useEffect(() => {
    const loadAffiliations = async () => {
      if (isAffiliationLocked && !customerFacing) {
        setAffiliations([fixedAffiliation]);
        setSelectedAff(fixedAffiliation);
        return;
      }

      try {
        const snap = await getDocs(collection(db, "affiliations"));
        let list = snap.docs.map((d) => d.id).filter(Boolean);

        if (list.length === 0) {
          const userSnap = await getDocs(collection(db, "users"));
          list = Array.from(
            new Set(userSnap.docs.map((d) => d.data().affiliation)),
          ).filter(Boolean);
        }

        list = list.filter((x) => x !== "총무노트" && x !== "버디");
        list.sort((a, b) => a.localeCompare(b, "ko"));
        setAffiliations(list);
        setSelectedAff(customerFacing ? fixedAffiliation : ALL_AFFILIATION);
      } catch (error) {
        console.error(error);
        toast.error("고객사 목록을 불러오지 못했습니다.");
      }
    };

    loadAffiliations();
  }, [customerFacing, fixedAffiliation, isAffiliationLocked]);

  useEffect(() => {
    if (!isAffiliationLocked) return;
    setSelectedAff(fixedAffiliation);
  }, [fixedAffiliation, isAffiliationLocked]);

  useEffect(() => {
    if (!customerFacing && forceAllAffiliations) {
      setSelectedAff(ALL_AFFILIATION);
    }
  }, [customerFacing, forceAllAffiliations]);

  useEffect(() => {
    if (!customerFacing && forceAllPeriods) {
      setRange(null);
    }
  }, [customerFacing, forceAllPeriods]);

  useEffect(() => {
    if (!customerFacing || !selectedAff || selectedAff === ALL_AFFILIATION) {
      setSavedMonthlyCuration(null);
      return;
    }

    let cancelled = false;

    const loadSavedMonthlyCuration = async () => {
      setLoadingSavedMonthlyCuration(true);
      setRunLogs([]);
      setDatasetSummary(null);
      setDebateResult(null);
      try {
        const saved = await getMonthlyCuration(selectedAff, selectedMonthKey);
        if (cancelled) return;

        setSavedMonthlyCuration(saved);
        if (saved) {
          setRequirement(saved.requirement || "");
          setDatasetSummary(saved.dataset_summary);
          setDebateResult(saved.debate_result);
        } else {
          setRequirement("");
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          toast.error("저장된 월별 큐레이션을 불러오지 못했습니다.");
        }
      } finally {
        if (!cancelled) {
          setLoadingSavedMonthlyCuration(false);
        }
      }
    };

    loadSavedMonthlyCuration();

    return () => {
      cancelled = true;
    };
  }, [customerFacing, selectedAff, selectedMonthKey]);

  const isCustomerView =
    customerFacing || (hideFilters && forceAllAffiliations && forceAllPeriods);
  const effectiveRange = customerFacing ? null : range;
  const tableRows = useMemo<CurationTableRow[]>(() => {
    if (!debateResult) return [];
    return debateResult.finalConsensus.items.map((item, index) => ({
      key: index + 1,
      rank: index + 1,
      ...item,
      reason: isCustomerView
        ? sanitizeCustomerFacingReason(item.reason)
        : item.reason,
    }));
  }, [debateResult, isCustomerView]);
  const tableColumns = isCustomerView
    ? CUSTOMER_CURATION_TABLE_COLUMNS
    : CURATION_TABLE_COLUMNS;
  const tableSearch = isCustomerView
    ? {
        keys: [
          "product_name",
          "category",
          "reference_source",
          "reason",
          "coupang_search_url",
        ],
        placeholder: "품목/참조소스/추천근거 검색",
      }
    : {
        keys: [
          "product_name",
          "category",
          "reference_source",
          "reason",
          "coupang_search_url",
        ],
        placeholder: "품목/참조소스/근거 검색",
      };
  const periodSummaryText = datasetSummary
    ? datasetSummary.period_mode === "all"
      ? "전체 기간"
      : `${datasetSummary.period_start} ~ ${datasetSummary.period_end}`
    : customerFacing
      ? "전체 기간"
      : forceAllPeriods
      ? "전체 기간"
      : range
        ? `${range[0].format("YYYY-MM-DD")} ~ ${range[1].format("YYYY-MM-DD")}`
        : "-";
  const savedMonthLabel = savedMonthlyCuration
    ? `${savedMonthlyCuration.year}년 ${savedMonthlyCuration.month}월`
    : "";

  const runningFrame = RUNNING_FRAMES[runningFrameIndex % RUNNING_FRAMES.length];
  const runningDots = ".".repeat((runningFrameIndex % 3) + 1);

  const handleRun = async () => {
    if (!selectedAff) {
      toast.error("고객사를 선택해 주세요.");
      return;
    }
    if (!customerFacing && !forceAllPeriods && (!range?.[0] || !range?.[1])) {
      toast.error("기간을 선택해 주세요.");
      return;
    }
    if (!openAIEnabled && !geminiApiKey) {
      toast.error("Gemini API 키 또는 OpenAI 프록시 사용 설정이 필요합니다.");
      return;
    }

    try {
      setRunning(true);
      setRunningFrameIndex(0);
      setActiveStepMessage("실행 준비");
      setDebateResult(null);
      clearLogs();
      appendLog("info", "큐레이션 실행 시작");

      const targetAffiliations =
        customerFacing
          ? affiliations
          : selectedAff === ALL_AFFILIATION
            ? affiliations
            : [selectedAff];
      if (targetAffiliations.length === 0) {
        toast.error("큐레이션할 고객사 데이터가 없습니다.");
        setDatasetSummary(null);
        appendLog("error", "고객사 데이터가 없어 실행 중단");
        return;
      }
      if (customerFacing) {
        appendLog("info", `결과 저장 고객사: ${selectedAff}`);
        appendLog(
          "info",
          `판매 데이터 대상 고객사: 전체(${targetAffiliations.length}개)`,
        );
      } else {
        appendLog(
          "info",
          `대상 고객사: ${
            forceAllAffiliations || selectedAff === ALL_AFFILIATION
              ? `전체(${targetAffiliations.length}개)`
              : selectedAff
          }`,
        );
      }
      appendLog(
        "info",
        customerFacing
          ? `저장 월: ${selectedMonthKey} / 판매 데이터 범위: 전체 기간`
          : forceAllPeriods || !range
            ? "기간: 전체"
            : `기간: ${range[0].format("YYYY-MM-DD")} ~ ${range[1].format("YYYY-MM-DD")}`,
      );

      setActiveStepMessage("판매 데이터 로딩");
      appendLog("info", "판매 데이터 로딩 중...");
      const rows = await loadSalesRows(targetAffiliations, effectiveRange);
      if (rows.length === 0) {
        toast.warning(
          customerFacing
            ? "판매데이터를 찾지 못했습니다."
            : "선택한 조건에서 판매데이터를 찾지 못했습니다.",
        );
        setDatasetSummary(null);
        appendLog("warn", "판매 데이터 0건으로 실행 중단");
        return;
      }
      appendLog("success", `판매 데이터 로딩 완료 (${rows.length}건)`);

      setActiveStepMessage("보조 시트 데이터 로딩");
      appendLog(
        "info",
        customerFacing
          ? "입고/출고/간식신청서 데이터 로딩 중..."
          : "입고/출고/간식신청서/마진 데이터 로딩 중...",
      );
      const supplementalData = await loadSupplementalCurationData(
        rows.map((row) => row.productName),
      );
      if (supplementalData.failed_sources.length > 0) {
        appendLog(
          "warn",
          `보조 데이터 일부 로딩 실패 (${supplementalData.failed_sources.join(", ")})`,
        );
      }
      appendLog(
        "success",
        customerFacing
          ? `보조 데이터 로딩 완료 (입고 ${supplementalData.inbound_rows}행, 출고 ${supplementalData.outbound_rows}행, 잔여 ${supplementalData.remaining_stock_total_qty.toLocaleString("ko-KR")}개, 신청 ${supplementalData.request_rows}행)`
          : `보조 데이터 로딩 완료 (입고 ${supplementalData.inbound_rows}행, 출고 ${supplementalData.outbound_rows}행, 잔여 ${supplementalData.remaining_stock_total_qty.toLocaleString("ko-KR")}개, 신청 ${supplementalData.request_rows}행, 마진 ${supplementalData.markup_rows}행)`,
      );

      const baseSummary = buildDatasetSummary(
        selectedAff,
        effectiveRange,
        rows,
        [],
        supplementalData,
      );
      appendLog(
        "info",
        `상품 집계 완료 (상위 후보 ${baseSummary.top_products.length}개)`,
      );
      setActiveStepMessage("외부 참조 데이터 확인");
      appendLog(
        "info",
        "외부 참조 데이터 확인 중 (쿠팡/네이버스토어/마켓컬리/11번가/G마켓)...",
      );
      const coupangLoad = await loadCoupangReferences(baseSummary.top_products);
      const {
        references: coupangReferences,
        rateLimited,
        attempted,
        fromCacheCount,
        staleCacheCount,
        networkCallCount,
      } = coupangLoad;
      if (coupangReferences.length === 0) {
        appendLog(
          "warn",
          "외부 참조 데이터 0건. 이번 실행은 판매데이터 기반으로 계속 진행",
        );
        toast.warning(
          "외부 참조 데이터를 가져오지 못해 판매데이터 기반으로만 진행합니다.",
        );
      } else {
        const sourceCounts = coupangReferences.reduce<Record<string, number>>(
          (acc, ref) => {
            acc[ref.source_label] = (acc[ref.source_label] || 0) + 1;
            return acc;
          },
          {},
        );
        const sourceSummary = Object.entries(sourceCounts)
          .map(([name, count]) => `${name} ${count}`)
          .join(", ");
        appendLog(
          "success",
          `외부 참조 확보 ${coupangReferences.length}건 [${sourceSummary}] (캐시 ${fromCacheCount}, 구캐시 ${staleCacheCount}, 네트워크 ${networkCallCount}, 검토 ${attempted})`,
        );
      }

      if (rateLimited) {
        appendLog(
          "warn",
          `일부 소스 요청 제한 발생. 현재 확보된 참조로 계속 진행 (${coupangReferences.length}/${attempted})`,
        );
        toast.warning(
          `외부 참조 요청 제한으로 일부 품목만 반영했습니다 (${coupangReferences.length}/${attempted}).`,
        );
      }

      const summary = buildDatasetSummary(
        selectedAff,
        effectiveRange,
        rows,
        coupangReferences,
        supplementalData,
      );
      setDatasetSummary(summary);
      appendLog(
        "info",
        customerFacing
          ? `토론 입력 생성 완료 (판매 + 입출고 잔여 + 신청 + 외부참조 ${summary.coupang_references.length}건)`
          : `토론 입력 생성 완료 (판매 + 입출고 잔여 + 신청/마진 + 외부참조 ${summary.coupang_references.length}건)`,
      );
      setActiveStepMessage("AI 토론 실행");

      const result = await runDebate({
        requirement: requirement.trim(),
        datasetSummary: summary,
        openAIEnabled,
        openAIApiKey,
        openAIModel,
        openAIFallbackModel,
        geminiApiKey,
        geminiModel,
        geminiFallbackModel,
        customerFacing: isCustomerView,
        onLog: appendLog,
        onProgress: (message) => {
          if (!message) {
            setActiveStepMessage("결과 정리");
            return;
          }
          setActiveStepMessage(message);
        },
      });
      setDebateResult(result);
      appendLog("success", "큐레이션 실행 완료");
      toast.success("AI 토론 기반 큐레이션이 완료되었습니다.");
    } catch (error: any) {
      console.error(error);
      appendLog("error", error?.message || "큐레이션 생성 중 오류");
      toast.error(error?.message || "큐레이션 생성 중 오류가 발생했습니다.");
    } finally {
      setActiveStepMessage("");
      setRunning(false);
    }
  };

  const formatSavedAt = (value: any) => {
    if (!value) return "-";
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : Number.isFinite(value?.seconds)
          ? new Date(value.seconds * 1000)
          : value instanceof Date
            ? value
            : null;
    if (!date) return "-";
    return dayjs(date).format("YYYY-MM-DD HH:mm");
  };

  const handleSaveMonthlyResult = async () => {
    if (!customerFacing) return;
    if (!selectedAff) {
      toast.error("고객사 정보가 없습니다.");
      return;
    }
    if (!datasetSummary || !debateResult) {
      toast.error("먼저 큐레이션을 실행해 주세요.");
      return;
    }

    try {
      setSavingMonthlyCuration(true);
      const saved = await saveMonthlyCuration({
        affiliation: selectedAff,
        monthKey: selectedMonthKey,
        requirement: requirement.trim(),
        datasetSummary,
        debateResult,
        customerFacing: true,
      });
      setSavedMonthlyCuration(saved.record);
      toast.success(
        saved.existed
          ? `${selectedMonthKey} 큐레이션 결과를 수정 저장했습니다.`
          : `${selectedMonthKey} 큐레이션 결과를 저장했습니다.`,
      );
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || "큐레이션 결과 저장에 실패했습니다.");
    } finally {
      setSavingMonthlyCuration(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold">AI 큐레이션</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {customerFacing
            ? "전체 판매데이터를 기본으로 보고, 입고/출고/간식신청서와 외부 가격 참조를 함께 반영해 월별 저장용 스낵/음료 큐레이션을 제안합니다."
            : "판매데이터를 기본으로 보고, 입고/출고/간식신청서/마진 데이터와 외부 가격 참조(쿠팡/네이버스토어/마켓컬리/11번가/G마켓)를 함께 반영해 ChatGPT와 Gemini가 토론한 최종 스낵/음료 리스트를 제안합니다."}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isCustomerView ? "자연어 요구사항" : "큐레이션 조건"}</CardTitle>
          <CardDescription>
            {customerFacing
              ? "전체 판매데이터를 기준으로 큐레이션을 생성하고, 선택한 월 키로 결과를 저장합니다."
              : isCustomerView
                ? "전체 고객사, 전체 기간 판매데이터를 기본으로 보고 보조 시트와 외부 참조를 함께 반영합니다."
              : "예산, 구성 비율 등 자연어 조건을 입력하면 판매데이터와 입출고 기반 잔여수량, 신청서, 마진, 외부 참조를 함께 반영합니다."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {customerFacing ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <div className="text-sm font-medium">결과 저장 고객사</div>
                <div className="flex h-10 items-center rounded-md border border-input bg-muted/30 px-3 text-sm font-medium">
                  {selectedAff || "-"}
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">저장 월</div>
                <input
                  type="month"
                  value={selectedMonthKey}
                  onChange={(e) => setSelectedMonthKey(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </div>
          ) : !hideFilters ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <div className="text-sm font-medium">고객사</div>
                {isAffiliationLocked ? (
                  <div className="flex h-10 items-center rounded-md border border-input bg-muted/30 px-3 text-sm font-medium">
                    {fixedAffiliation}
                  </div>
                ) : (
                  <Select value={selectedAff} onValueChange={setSelectedAff}>
                    <SelectTrigger>
                      <SelectValue placeholder="고객사 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_AFFILIATION}>
                        {ALL_AFFILIATION}
                      </SelectItem>
                      {affiliations.map((aff) => (
                        <SelectItem key={aff} value={aff}>
                          {aff}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">기간</div>
                <DateRangePicker
                  value={range}
                  onChange={(next) => next && setRange(next)}
                />
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-dashed bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
              기준 범위: 전체 고객사 / 전체 기간
            </div>
          )}

          {customerFacing && (
            <div className="rounded-md border border-dashed bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
              <div>
                판매 데이터 범위: 전체 고객사 / 전체 기간
              </div>
              {loadingSavedMonthlyCuration
                ? "저장된 월별 큐레이션을 확인하는 중입니다."
                : savedMonthlyCuration
                  ? `${savedMonthLabel} 큐레이션이 이미 저장되어 있습니다. 이번 월은 새 문서를 추가하지 않고 수정 저장만 가능합니다. 마지막 저장: ${formatSavedAt(
                      savedMonthlyCuration.updated_at,
                    )}`
                  : `${selectedMonthKey} 월은 아직 저장된 큐레이션이 없습니다. 실행 후 "큐레이션 결과 확정하기"로 월별 결과를 저장하세요.`}
            </div>
          )}

          <div className="space-y-2">
            <div className="text-sm font-medium">자연어 요구사항</div>
            <textarea
              value={requirement}
              onChange={(e) => setRequirement(e.target.value)}
              placeholder="예: 예산 40만원 정도의 스낵 리스트를 짜줘. 또는 300만원 이내로 스낵/음료 비율 6:4로 구성해줘."
              className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground">
              모델: ChatGPT `{openAIModel}` (실패 시 `{openAIFallbackModel}`)
              / Gemini `{geminiModel}` / 외부 참조: 쿠팡, 네이버스토어,
              마켓컬리, 11번가, G마켓 / OpenAI는 Firebase 함수 프록시로
              호출, 최종 실패 시 Gemini로 자동 대체
            </div>
            <Button
              onClick={handleRun}
              disabled={running || loadingSavedMonthlyCuration}
            >
              {running ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner size="sm" /> 토론 중...
                </span>
              ) : customerFacing && savedMonthlyCuration ? (
                "AI 큐레이션 다시 생성"
              ) : (
                "AI 큐레이션 실행"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {(running || runLogs.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>실행 로그</CardTitle>
            <CardDescription>
              큐레이션 진행 상태와 단계별 결과를 순차적으로 표시합니다.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              ref={logContainerRef}
              className="max-h-[260px] space-y-2 overflow-y-auto rounded-md border bg-muted/20 p-3"
            >
              {runLogs.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  실행 로그를 준비 중입니다...
                </div>
              ) : (
                runLogs.map((log) => {
                  const levelClass =
                    log.level === "success"
                      ? "text-emerald-600"
                      : log.level === "warn"
                        ? "text-amber-600"
                        : log.level === "error"
                          ? "text-red-600"
                          : "text-slate-600";
                  return (
                    <div
                      key={log.id}
                      className="flex items-start gap-2 text-sm font-mono"
                    >
                      <span className="text-muted-foreground">[{log.at}]</span>
                      <span className={levelClass}>{LOG_LEVEL_TEXT[log.level]}</span>
                      <span className="break-words">{log.message}</span>
                    </div>
                  );
                })
              )}
              {running && activeStepMessage && (
                <div className="flex items-center gap-2 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-sm font-mono text-blue-700">
                  <span className="w-4 text-center">{runningFrame}</span>
                  <span className="break-words">
                    진행중: {activeStepMessage}
                    {runningDots}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {datasetSummary && (
        <Card>
          <CardHeader>
            <CardTitle>분석 데이터 요약</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm md:grid-cols-5">
            <div>
              <div className="text-muted-foreground">대상 고객사</div>
              <div className="font-semibold">{datasetSummary.affiliation}</div>
            </div>
            <div>
              <div className="text-muted-foreground">분석 기간</div>
              <div className="font-semibold">{periodSummaryText}</div>
            </div>
            <div>
              <div className="text-muted-foreground">판매행 수</div>
              <div className="font-semibold">
                {datasetSummary.source_rows.toLocaleString("ko-KR")}건
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">영수증 수</div>
              <div className="font-semibold">
                {datasetSummary.source_receipts.toLocaleString("ko-KR")}건
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">총 판매수량</div>
              <div className="font-semibold">
                {datasetSummary.source_total_qty.toLocaleString("ko-KR")}개
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">총 매출</div>
              <div className="font-semibold">
                {money(datasetSummary.source_total_sales)}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">외부 참조 품목</div>
              <div className="font-semibold">
                {datasetSummary.coupang_references.length.toLocaleString("ko-KR")}개
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">입고 데이터</div>
              <div className="font-semibold">
                {datasetSummary.inbound_rows.toLocaleString("ko-KR")}행
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">출고 데이터</div>
              <div className="font-semibold">
                {datasetSummary.outbound_rows.toLocaleString("ko-KR")}행
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">간식신청서</div>
              <div className="font-semibold">
                {datasetSummary.request_rows.toLocaleString("ko-KR")}행
              </div>
            </div>
            {!customerFacing && (
              <div>
                <div className="text-muted-foreground">마진 시트</div>
                <div className="font-semibold">
                  {datasetSummary.markup_rows.toLocaleString("ko-KR")}행
                </div>
              </div>
            )}
            <div>
              <div className="text-muted-foreground">입출고 잔여 품목</div>
              <div className="font-semibold">
                {datasetSummary.remaining_stock_items.toLocaleString("ko-KR")}개
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">입출고 잔여 수량</div>
              <div className="font-semibold">
                {datasetSummary.remaining_stock_total_qty.toLocaleString("ko-KR")}
                개
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {debateResult && (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>목표 예산</CardDescription>
                <CardTitle className="text-2xl">
                  {debateResult.finalConsensus.target_budget
                    ? money(debateResult.finalConsensus.target_budget)
                    : "-"}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>예상 총액</CardDescription>
                <CardTitle className="text-2xl">
                  {money(debateResult.finalConsensus.estimated_total_cost)}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>스낵:음료 비율(비용 기준)</CardDescription>
                <CardTitle className="text-2xl">
                  {debateResult.finalConsensus.snack_beverage_ratio || "-"}
                </CardTitle>
              </CardHeader>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>토론 요약</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <div className="text-sm font-semibold">ChatGPT 초안 의견</div>
                <p className="text-sm text-muted-foreground">
                  {debateResult.chatgptDraft.opinion || "-"}
                </p>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-semibold">Gemini 수정 의견</div>
                <p className="text-sm text-muted-foreground">
                  {debateResult.geminiRevision.opinion || "-"}
                </p>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-semibold">최종 합의 결론</div>
                <p className="text-sm text-muted-foreground">
                  {debateResult.finalConsensus.discussion_summary || "-"}
                </p>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-semibold">최종 가정</div>
                <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                  {debateResult.finalConsensus.assumptions.length > 0 ? (
                    debateResult.finalConsensus.assumptions.map((assumption, i) => (
                      <li key={`${assumption}-${i}`}>{assumption}</li>
                    ))
                  ) : (
                    <li>가정 정보 없음</li>
                  )}
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>최종 큐레이션 리스트</CardTitle>
              <CardDescription>
                {customerFacing
                  ? "판매/입출고/간식신청서 데이터와 자연어 요구조건을 함께 반영한 월별 큐레이션 결과입니다."
                  : "판매/입출고/간식신청서/마진 데이터와 자연어 요구조건을 함께 반영한 테이블 결과입니다."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={tableColumns}
                dataSource={tableRows}
                pagination={false}
                search={tableSearch}
              />
              {customerFacing && (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/20 px-4 py-3">
                  <div className="text-sm text-muted-foreground">
                    {savedMonthlyCuration
                      ? `${savedMonthLabel} 큐레이션은 이미 저장되어 있습니다. 이번 확정은 기존 월별 결과를 수정합니다.`
                      : `${selectedMonthKey} 큐레이션 결과를 확정하면 결과/분석 메뉴에서 월별로 확인할 수 있습니다.`}
                  </div>
                  <Button
                    onClick={handleSaveMonthlyResult}
                    disabled={
                      running ||
                      savingMonthlyCuration ||
                      loadingSavedMonthlyCuration
                    }
                  >
                    {savingMonthlyCuration ? (
                      <span className="inline-flex items-center gap-2">
                        <Spinner size="sm" /> 저장 중...
                      </span>
                    ) : savedMonthlyCuration ? (
                      "큐레이션 결과 수정 저장"
                    ) : (
                      "큐레이션 결과 확정하기"
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AICurationPage;
